<glxy-table-container
  class="product-selector-table-container"
  [columns]="columns"
  [dataSource]="tableDataSource"
  [pageSize]="configuration.pageSize"
  [pageSizeOptions]="[15, 30, 50]"
>
  <glxy-table-content-header
    #tableHeader
    [showColumnArrange]="false"
    [showSearch]="true"
    [showFilters]="true"
    [showFiltersButton]="false"
    [showFiltersOpen]="!!requiredItem()"
    [showSort]="false"
    [showActions]="false"
  >
    <div *ngIf="configuration?.itemTypesAvailable?.length > 1" filter-button class="filter-toggle">
      <mat-button-toggle-group class="item-type-filter">
        <mat-button-toggle
          value="products"
          checked
          (change)="setItemType($event.value)"
          data-cy="item-type-filter-toggle-products"
        >
          {{ 'INVENTORY_UI.COMMON.ITEM_TYPE.PRODUCTS' | translate }}
        </mat-button-toggle>
        <mat-button-toggle
          value="packages"
          (change)="setItemType($event.value)"
          data-cy="item-type-filter-toggle-packages"
        >
          {{ 'INVENTORY_UI.COMMON.ITEM_TYPE.PACKAGES' | translate }}
        </mat-button-toggle>
      </mat-button-toggle-group>
    </div>

    <div after-search class="after-search-container">
      <mat-checkbox
        *ngIf="itemType() === 'products' || itemType() === 'allItems'"
        data-cy="show-addonitems-checkbox"
        [checked]="showAddons()"
        (click)="setShowAddons(!showAddons())"
      >
        {{ 'INVENTORY_UI.ITEM_SELECTOR.SHOW_ADDONS' | translate }}
      </mat-checkbox>

      <mat-checkbox
        data-cy="show-unavailable-checkbox"
        [checked]="showUnavailableItems()"
        (click)="setShowUnavailableItems(!showUnavailableItems())"
      >
        {{ 'INVENTORY_UI.ITEM_SELECTOR.SHOW_UNAVAILABLE' | translate }}
      </mat-checkbox>
    </div>

    <div filters-area data-cy="selector-filter-chips">
      <mat-chip data-cy="selector-filter-chip" *ngIf="!!requiredItem()" (removed)="unsetRequiredItem()">
        Add-ons: {{ requiredItem()?.sellerWhitelabelName ?? requiredItem()?.defaultName ?? requiredItem()?.itemId }}
        <button matChipRemove>
          <mat-icon>cancel</mat-icon>
        </button>
      </mat-chip>
    </div>
  </glxy-table-content-header>

  <table mat-table>
    <tr mat-header-row *matHeaderRowDef="[]"></tr>

    <!-- Checkbox Column -->
    <ng-container matColumnDef="checkbox">
      <th mat-header-cell *matHeaderCellDef data-cy="header-cell"></th>
      <td mat-cell *matCellDef="let item" class="checkbox-cell">
        <div
          *ngIf="!dependenciesLoading; else loadingSpinner"
          [glxyTooltip]="item | formatDisabledItem: configuration.country : 'tooltip'"
          [glxyTooltipDisabled]="!(item | isItemDisabled: configuration.hideQuantity)"
          [highContrast]="false"
        >
          <mat-checkbox
            #checkbox
            data-cy="select-item-checkbox"
            [checked]="item.isSelected"
            [disabled]="item | isItemDisabled: configuration.hideQuantity"
            (click)="handleSelectionChange(!item.isSelected, item)"
          ></mat-checkbox>
        </div>

        <ng-template #loadingSpinner>
          <glxy-loading-spinner [size]="'small'"></glxy-loading-spinner>
        </ng-template>
      </td>
    </ng-container>

    <!-- Name Column -->
    <ng-container matColumnDef="name">
      <th mat-header-cell *matHeaderCellDef data-cy="header-cell">
        {{ 'INVENTORY_UI.ITEM_SELECTOR.COLUMN.NAME' | translate }}
      </th>
      <td mat-cell *matCellDef="let item">
        <div class="product-cell">
          <glxy-avatar
            data-cy="product-icon"
            [width]="32"
            [name]="item?.sellerWhitelabelName ?? item?.defaultName"
            [src]="item?.sellerWhitelabelIconUrl ?? item?.iconUrl"
          ></glxy-avatar>
          <div class="product-name-type">
            <div class="product-edition">
              <div class="product-name">
                {{ item?.sellerWhitelabelName ?? item?.defaultName }}
              </div>
              <div *ngIf="item?.usesEditions">
                <inventory-ui-edition-selector
                  *ngIf="item?.editions"
                  [selectedEditionId]="item?.editionId"
                  [editions]="item.editions"
                  (editionSelectionChange)="handleEditionChange($event, item)"
                ></inventory-ui-edition-selector>
                <span *ngIf="!item?.editions" class="stencil-shimmer edition-shimmer"></span>
              </div>
            </div>

            <div>
              <ng-container
                *ngIf="
                  configuration.hideOwnerName || item.itemType === ItemType.ITEM_TYPE_PACKAGE;
                  else itemTypeByOwner
                "
              >
                <ng-container *ngIf="item.itemType === ItemType.ITEM_TYPE_PACKAGE; else itemTypeOnly">
                  <inventory-ui-package-contents-popover
                    [packageId]="item?.itemId"
                    [partnerId]="configuration.partnerId"
                    [marketId]="configuration.marketId"
                  />
                </ng-container>
                <ng-template #itemTypeOnly>
                  {{ item | formatProductType }}
                </ng-template>
              </ng-container>
              <ng-template #itemTypeByOwner>
                {{
                  'INVENTORY_UI.ITEM_SELECTOR.ITEM_TYPE_BY_OWNER'
                    | translate: { itemType: item | formatProductType, owner: item.ownerName }
                }}
              </ng-template>
            </div>

            <div *ngIf="item | isItemDisabled: configuration.hideQuantity; else productRequirements">
              {{ item | formatDisabledItem: configuration.country : 'subtext' }}
            </div>

            <ng-template #productRequirements>
              <div
                *ngIf="item?.requiredItems?.length"
                (mouseenter)="productRequirementPopover.isOpen = item.requiredItems.length > 1"
                (mouseleave)="productRequirementPopover.isOpen = false"
                [glxyPopover]="productRequirementPopover"
              >
                {{ item | formatProductRequirement }}
              </div>

              <glxy-popover #productRequirementPopover [positions]="[PopoverPositions.Right]">
                <p>{{ 'INVENTORY_UI.ITEM_SELECTOR.REQUIRED_ITEMS_LABEL' | translate }}</p>
                <ul>
                  <li *ngFor="let requiredItem of item?.requiredItems">
                    {{ requiredItem.name }}
                  </li>
                </ul>
              </glxy-popover>
            </ng-template>
            <mat-chip
              *ngIf="item.itemType === ItemType.ITEM_TYPE_APP && item.childApps?.length > 0"
              data-cy="addon-filter-chip"
              (click)="setRequiredItem(item)"
              [trackEvent]="{
                eventName: 'item-selector-view-addons',
                category: 'chip',
                action: 'click'
              }"
            >
              {{ 'INVENTORY_UI.ITEM_SELECTOR.VIEW_ADDONS' | translate }}
            </mat-chip>
          </div>
        </div>
      </td>
    </ng-container>

    <!-- Quantity Input Column -->
    <ng-container matColumnDef="quantityInput">
      <th mat-header-cell *matHeaderCellDef data-cy="header-cell">
        {{ 'INVENTORY_UI.ITEM_SELECTOR.COLUMN.QUANTITY' | translate }}
      </th>
      <td mat-cell class="quantity-column" *matCellDef="let item">
        <glxy-form-field
          *ngIf="
            item?.isMultiactivatable && item?.itemType === ItemType.ITEM_TYPE_ADDON && !this.configuration.hideQuantity
          "
        >
          <input
            data-cy="quantity-input"
            matInput
            type="number"
            dense
            min="0"
            [value]="item?.quantity"
            (change)="handleQuantityChange($event, item)"
          />
        </glxy-form-field>
      </td>
    </ng-container>

    <!-- Wholesale Column -->
    <ng-container matColumnDef="wholesale">
      <th mat-header-cell *matHeaderCellDef data-cy="header-cell">
        {{ 'INVENTORY_UI.ITEM_SELECTOR.COLUMN.WHOLESALE' | translate }}
      </th>
      <td mat-cell *matCellDef="let item">
        <ng-container *ngIf="item.pricesInitialized; else priceShimmer">
          <billing-ui-price-display
            *ngIf="item?.appPrices?.pricesForContexts?.['wholesale']"
            [align]="'end'"
            [currencyCode]="item | parseWholesaleCurrency"
            [appPrice]="item.appPrices.pricesForContexts['wholesale']"
            [editionId]="item?.editionId"
            [priceType]="item?.priceType"
          ></billing-ui-price-display>
        </ng-container>
      </td>
    </ng-container>

    <!-- Retail Column -->
    <ng-container matColumnDef="retail">
      <th mat-header-cell *matHeaderCellDef data-cy="header-cell">
        {{ 'INVENTORY_UI.ITEM_SELECTOR.COLUMN.RETAIL' | translate }}
      </th>
      <td mat-cell *matCellDef="let item">
        <ng-container *ngIf="item.pricesInitialized; else priceShimmer">
          <billing-ui-price-display
            *ngIf="item?.appPrices?.pricesForContexts?.['retail']"
            [align]="'end'"
            [appPrice]="item.appPrices.pricesForContexts['retail']"
            [editionId]="item?.editionId"
          ></billing-ui-price-display>
        </ng-container>
      </td>
    </ng-container>

    <ng-template #priceShimmer>
      <div class="stencil-shimmer shimmer"></div>
    </ng-template>

    <tr
      mat-row
      *matRowDef="let row; columns: []"
      data-cy="table-row"
      [ngClass]="{ 'mat-row-disabled': row.isSuspended || row.unavailableInCountry }"
    ></tr>
  </table>
</glxy-table-container>
